/**
 * Text a company owns that has to read correctly in more than one language.
 *
 * Distinct from both of the language mechanisms that came before it, and the
 * gap between them is why this exists:
 *
 * - **i18next resources** translate the app's own *chrome* — labels, buttons,
 *   headings. They are shipped with the build, so they can be translated once
 *   for everybody.
 * - **`companies.language`** decides which language the company *sends* in,
 *   and `seed-templates.<lang>.ts` wrote the starter library in that language
 *   once, at signup.
 *
 * Neither covers a room named "Kitchen" on a crew sheet a cleaner reads in
 * Spanish. That name is a *row*, editable from Settings, so it cannot be
 * translated at build time; and it is read by two audiences at once — the
 * office and crew reading the app in whatever language their browser reports,
 * and the customer reading a quote written in the company's language. One
 * string cannot serve both, so the row carries a small map instead: language
 * tag to text.
 *
 * Stored as JSON in a sibling column (`name` / `name_i18n`) rather than as a
 * side table, because it is always read with its row and never queried across
 * rows. The base column keeps its original meaning — the company's own wording
 * in its own language — so every reader that has not learned about the map
 * still shows exactly what it showed before.
 */

import { SUPPORTED_LANGUAGES, type SupportedLanguage } from './languages.ts';

/**
 * One text in each language the company has filled in.
 *
 * Partial on purpose: an owner who renames a room type in English and has not
 * got to the Spanish yet has an entry for one of the two, and the missing one
 * resolves to the base column rather than to a blank.
 */
export type LocalizedText = Partial<Record<SupportedLanguage, string>>;

function isSupported(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Trims, drops blanks, and drops languages this build does not have.
 *
 * Blank and absent have to mean the same thing here, or clearing a Spanish
 * name in the form would store `''` and resolve to an empty room heading
 * instead of falling back to the base text the way an untranslated row does.
 */
export function normalizeLocalizedText(value: LocalizedText | null | undefined): LocalizedText | null {
  if (!value) return null;

  const normalized: LocalizedText = {};
  let count = 0;
  for (const [language, text] of Object.entries(value)) {
    if (!isSupported(language) || typeof text !== 'string') continue;
    const trimmed = text.trim();
    if (trimmed.length === 0) continue;
    normalized[language] = trimmed;
    count += 1;
  }

  return count === 0 ? null : normalized;
}

/**
 * Reads a `*_i18n` column.
 *
 * Never throws: this is stored data, and a row that somehow holds unparseable
 * JSON has to degrade to "no translations" — showing the base text — rather
 * than failing the quote page it appears on.
 */
export function parseLocalizedText(raw: string | null | undefined): LocalizedText | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return normalizeLocalizedText(parsed as LocalizedText);
  } catch {
    return null;
  }
}

/** Writes a `*_i18n` column. `null` when there is nothing to store. */
export function serializeLocalizedText(value: LocalizedText | null | undefined): string | null {
  const normalized = normalizeLocalizedText(value);
  return normalized === null ? null : JSON.stringify(normalized);
}

/**
 * The text to show a reader of `language`.
 *
 * The rule is deliberately two steps and no more: the translation if there is
 * one, otherwise the base column. There is no walk through the other languages
 * looking for *something* to print, because the base column is already the
 * company's own wording — reaching past it to a third language would put a
 * Spanish room name on an English quote whose owner never asked for one, which
 * is the half-translated document this whole area exists to avoid.
 */
export function localizedText(
  base: string,
  translations: LocalizedText | null | undefined,
  language: SupportedLanguage,
): string {
  return translations?.[language] ?? base;
}

/** As `localizedText`, for a column that is allowed to be empty. */
export function localizedOptionalText(
  base: string | null,
  translations: LocalizedText | null | undefined,
  language: SupportedLanguage,
): string | null {
  return translations?.[language] ?? base;
}

/**
 * The languages this text still needs, so a form can say so.
 *
 * A base column with no map at all counts as untranslated in every language
 * *except* the one given as `baseLanguage` — the row is written in something,
 * and that something is the company's language.
 */
export function missingLanguages(
  translations: LocalizedText | null | undefined,
  baseLanguage: SupportedLanguage,
): SupportedLanguage[] {
  return SUPPORTED_LANGUAGES.filter(
    (language) => language !== baseLanguage && !normalizeLocalizedText(translations)?.[language],
  );
}
